import { AlertCircle, CheckCircle, TrendingUp, XCircle } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { ATSAnalysis } from '../../types';
import LoadingOverlay from '../Common/LoadingOverlay';
import './ATSChecker.css';

const ATSChecker: React.FC = () => {
  const navigate = useNavigate();
  const { resumeId } = useParams<{ resumeId: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [analysis, setAnalysis] = useState<ATSAnalysis | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchATSAnalysis = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await api.checkATSScore(resumeId!);
      
      // Ensure all scores have valid numbers (defensive programming)
      const normalizedData: ATSAnalysis = {
        overallScore: data.overallScore || 0,
        category: data.category || 'Unknown',
        strengths: data.strengths || [],
        weaknesses: data.weaknesses || [],
        recommendations: data.recommendations || [],
        keywordAnalysis: {
          present: data.keywordAnalysis?.present || [],
          missing: data.keywordAnalysis?.missing || [],
          score: data.keywordAnalysis?.score ?? 0,
        },
        formattingScore: data.formattingScore ?? 0,
        contentScore: data.contentScore ?? 0,
        impactScore: data.impactScore ?? 0,
      };
      
      console.log('ATS Analysis received:', normalizedData);
      setAnalysis(normalizedData);
    } catch (err: any) {
      console.error('Failed to fetch ATS analysis:', err);
      setError(err.response?.data?.error || 'Failed to analyze resume');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (resumeId) {
      fetchATSAnalysis();
    }
    // fetchATSAnalysis is stable (from component scope) and we only want
    // to rerun when resumeId changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeId]);

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'var(--color-success)';
    if (score >= 75) return 'var(--color-primary-600)';
    if (score >= 60) return 'var(--color-warning)';
    return 'var(--color-error)';
  };

  const getPriorityColor = (priority: string) => {
    if (priority === 'High') return 'var(--color-error)';
    if (priority === 'Medium') return 'var(--color-warning)';
    return 'var(--color-text-secondary)';
  };

  if (isLoading) {
    return <LoadingOverlay message="Analyzing your resume with AI..." />;
  }

  if (error || !analysis) {
    return (
      <div className="ats-container">
        <div className="error-state">
          <XCircle size={48} />
          <h2>Failed to Analyze Resume</h2>
          <p>{error || 'Something went wrong'}</p>
          <button onClick={() => navigate('/dashboard')} className="btn btn-primary">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ats-container">
      <div className="ats-header">
        <h1>ATS Compatibility Score</h1>
        <p className="text-secondary">See how your resume performs against Applicant Tracking Systems—and remember, you've already practiced your interview answers</p>
      </div>

      <div className="ats-content">
        {/* Overall Score Card */}
        <div className="score-card">
          <div className="score-circle" style={{ borderColor: getScoreColor(analysis.overallScore) }}>
            <div className="score-value" style={{ color: getScoreColor(analysis.overallScore) }}>
              {analysis.overallScore}
            </div>
            <div className="score-label">ATS Score</div>
          </div>
          <div className="score-details">
            <h2 style={{ color: getScoreColor(analysis.overallScore) }}>{analysis.category}</h2>
            <p className="text-secondary">
              {analysis.overallScore >= 90 && 'Your resume is highly optimized for ATS systems'}
              {analysis.overallScore >= 75 && analysis.overallScore < 90 && 'Your resume is well-optimized with minor improvements needed'}
              {analysis.overallScore >= 60 && analysis.overallScore < 75 && 'Your resume needs improvements to pass ATS effectively'}
              {analysis.overallScore < 60 && 'Your resume requires significant changes for ATS compatibility'}
            </p>
          </div>
        </div>

        {/* Detailed Scores */}
        <div className="detailed-scores">
          <div className="metric-card">
            <div className="metric-label">Keywords</div>
            <div className="metric-value" style={{ color: getScoreColor(analysis.keywordAnalysis.score) }}>
              {analysis.keywordAnalysis.score}
            </div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Formatting</div>
            <div className="metric-value" style={{ color: getScoreColor(analysis.formattingScore) }}>
              {analysis.formattingScore}
            </div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Content</div>
            <div className="metric-value" style={{ color: getScoreColor(analysis.contentScore) }}>
              {analysis.contentScore}
            </div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Impact</div>
            <div className="metric-value" style={{ color: getScoreColor(analysis.impactScore) }}>
              {analysis.impactScore}
            </div>
          </div>
        </div>

        {/* Strengths & Weaknesses */}
        <div className="analysis-grid">
          <div className="analysis-section">
            <h3><CheckCircle size={20} /> Strengths</h3>
            <ul>
              {analysis.strengths.map((strength, index) => (
                <li key={index}>{strength}</li>
              ))}
            </ul>
          </div>
          <div className="analysis-section">
            <h3><AlertCircle size={20} /> Weaknesses</h3>
            <ul>
              {analysis.weaknesses.map((weakness, index) => (
                <li key={index}>{weakness}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* Keywords Analysis */}
        <div className="keywords-section">
          <h3><TrendingUp size={20} /> Keyword Analysis</h3>
          <div className="keywords-grid">
            <div>
              <h4>Present Keywords</h4>
              <div className="keyword-list">
                {analysis.keywordAnalysis.present.map((keyword, index) => (
                  <span key={index} className="keyword present">{keyword}</span>
                ))}
              </div>
            </div>
            <div>
              <h4>Missing Keywords</h4>
              <div className="keyword-list">
                {analysis.keywordAnalysis.missing.map((keyword, index) => (
                  <span key={index} className="keyword missing">{keyword}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Recommendations */}
        <div className="recommendations-section">
          <h3>Recommendations</h3>
          <div className="recommendations-list">
            {analysis.recommendations.map((rec, index) => (
              <div key={index} className="recommendation-item">
                <div className="recommendation-header">
                  <span className="recommendation-category">{rec.category}</span>
                  <span className="recommendation-priority" style={{ color: getPriorityColor(rec.priority) }}>
                    {rec.priority} Priority
                  </span>
                </div>
                <p>{rec.suggestion}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="ats-actions">
          <button onClick={() => navigate('/dashboard')} className="btn btn-secondary">
            Back to Dashboard
          </button>
          <button onClick={fetchATSAnalysis} className="btn btn-primary">
            Re-analyze Resume
          </button>
        </div>
      </div>
    </div>
  );
};

export default ATSChecker;
